import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PageForm, { FormField } from '../../commons/PageForm';
import { useAppDispatch, useAppSelector } from '../../store';
import { updateEntity } from './grupo-empresarial.reducer';

export interface GrupoEditarFormValues {
  alias: string;
  grpAliasEquilibrio: string;
  grpAliasDecomsa: string;
}

const fields: FormField<GrupoEditarFormValues>[] = [
  { name: 'alias', label: 'Alias', required: true, placeholder: 'Alias del grupo' },
  { name: 'grpAliasEquilibrio', label: 'Alias equilibrio', placeholder: 'Alias equilibrio' },
  { name: 'grpAliasDecomsa', label: 'Alias decomsa', placeholder: 'Alias decomsa' },
];

const ACCENT = '#6366f1';

export interface GrupoEditarModalProps {
  open: boolean;
  onClose: () => void;
  /** id del grupo en la URL / Redux */
  grupoId: string | undefined;
}

export default function GrupoEditarModal({ open, onClose, grupoId }: GrupoEditarModalProps) {
  const dispatch = useAppDispatch();
  const updating = useAppSelector(s => s.grupoEmpresarial.updating);
  const updateSuccess = useAppSelector(s => s.grupoEmpresarial.updateSuccess);
  const entity = useAppSelector(s => s.grupoEmpresarial.entity) as Record<string, any> | null;

  const [form, setForm] = useState<GrupoEditarFormValues>({
    alias: '',
    grpAliasEquilibrio: '',
    grpAliasDecomsa: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof GrupoEditarFormValues, string>>>({});
  const submittedRef = useRef(false);

  useLayoutEffect(() => {
    if (!open) {
      submittedRef.current = false;
      return;
    }
    submittedRef.current = false;
    setErrors({});
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !grupoId) return;
    const e = entity;
    if (e?.id != null && String(e.id) === String(grupoId)) {
      setForm({
        alias: String(e.alias ?? ''),
        grpAliasEquilibrio: String(e.grpAliasEquilibrio ?? ''),
        grpAliasDecomsa: String(e.grpAliasDecomsa ?? ''),
      });
    }
  }, [open, grupoId, entity]);

  useEffect(() => {
    if (!open) {
      submittedRef.current = false;
      return;
    }
    if (!submittedRef.current || updating) return;
    if (updateSuccess) {
      submittedRef.current = false;
      onClose();
    } else {
      submittedRef.current = false;
    }
  }, [open, updating, updateSuccess, onClose]);

  const handleFieldChange = (name: keyof GrupoEditarFormValues, value: string) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!grupoId || entity?.id == null) return;
    const errs: Partial<Record<keyof GrupoEditarFormValues, string>> = {};
    if (!form.alias.trim()) errs.alias = 'El alias es requerido';
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    submittedRef.current = true;
    dispatch(
      updateEntity({
        ...entity,
        id: entity.id,
        alias: form.alias.trim(),
        grpAliasEquilibrio: form.grpAliasEquilibrio.trim(),
        grpAliasDecomsa: form.grpAliasDecomsa.trim(),
      }),
    );
  };

  if (!open) return null;

  return createPortal(
    <div
      role="presentation"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.45)',
        zIndex: 100000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onMouseDown={ev => {
        if (ev.target === ev.currentTarget && !updating) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal
        aria-labelledby="grupo-editar-modal-title"
        style={{
          background: '#fff',
          borderRadius: 14,
          padding: '24px 28px',
          maxWidth: 520,
          width: '100%',
          maxHeight: 'min(90vh, 640px)',
          overflow: 'auto',
          boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
        }}
        onMouseDown={ev => ev.stopPropagation()}
      >
        <h2
          id="grupo-editar-modal-title"
          style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: '#0f172a' }}
        >
          Editar grupo empresarial
        </h2>
        <p style={{ margin: '0 0 20px', fontSize: 13, color: '#64748b', lineHeight: 1.45 }}>
          Modifique los datos del grupo y guarde los cambios.
        </p>
        <PageForm
          fields={fields}
          values={form}
          errors={errors}
          isNew={false}
          saving={updating}
          onFieldChange={handleFieldChange}
          onSubmit={handleSubmit}
          onCancel={onClose}
          accentColor={ACCENT}
          submitLabel="Guardar cambios"
          cancelLabel="Cancelar"
        />
      </div>
    </div>,
    document.body,
  );
}
